import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/shared/ui/select';
import {
  bulkBatteryCommand, bulkTuyaRecheck, bulkTuyaUnlock,
  bulkUpdateAssets, bulkDeleteAssets,
} from '@/shared/api/bulk';

/**
 * Catalogue des actions de masse du Monitoring, au format attendu par
 * `shared/components/BulkEditDialog`.
 *
 * Sorti de la page pour deux raisons : MonitoringPage dépassait largement les 500
 * lignes, et surtout ces actions ne parlent pas toutes au même endpoint. Les commandes
 * MQTT ne concernent que les batteries, Tuya que les autres types : chaque action
 * déclare donc son `scope`, et le dialogue affiche combien d'éléments elle va vraiment
 * toucher. Avant, on cochait douze actifs, quatre partaient, et rien ne le disait.
 */

const batteries = items => items.filter(a => a.type === 'Batterie');
const devices = items => items.filter(a => a.type !== 'Batterie');

const plural = (n, mot) => `${n} ${mot}${n > 1 ? 's' : ''}`;

const batteryScope = items => {
  const ids = batteries(items).map(a => a.id);
  return { ids, note: `${plural(ids.length, 'batterie')} concernée${ids.length > 1 ? 's' : ''}` };
};

const deviceScope = items => {
  const ids = devices(items).map(a => a.id);
  return { ids, note: `${plural(ids.length, 'équipement')} Tuya concerné${ids.length > 1 ? 's' : ''}` };
};

/** Champs de la modification en masse : seul ce qui est rempli est appliqué. */
function renderEditForm(value, setValue) {
  const set = (k, v) => setValue({ ...value, [k]: v });
  return (
    <div className="space-y-3">
      <p className="text-[11px] text-neutral-500">
        Seuls les champs renseignés sont appliqués. Les autres restent inchangés.
      </p>
      <div>
        <Label className="text-[11px] text-neutral-500">Marque</Label>
        <Input
          className="h-9" placeholder="inchangée" value={value.marque || ''}
          onChange={e => set('marque', e.target.value)}
          data-testid="bulk-edit-marque"
        />
      </div>
      <div>
        <Label className="text-[11px] text-neutral-500">Modèle</Label>
        <Input
          className="h-9" placeholder="inchangé" value={value.modele || ''}
          onChange={e => set('modele', e.target.value)}
          data-testid="bulk-edit-modele"
        />
      </div>
      <div>
        <Label className="text-[11px] text-neutral-500">Statut mandat</Label>
        <Select
          value={value.statut_mandat || 'unchanged'}
          onValueChange={v => set('statut_mandat', v === 'unchanged' ? '' : v)}
        >
          <SelectTrigger className="h-9" data-testid="bulk-edit-statut-mandat">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unchanged">Inchangé</SelectItem>
            <SelectItem value="actif">Actif</SelectItem>
            <SelectItem value="signe">Signé</SelectItem>
            <SelectItem value="non_signe">Non signé</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

const ASSET_BULK_ACTIONS = {
  charge: {
    label: 'Démarrer charge',
    family: 'value',
    value_unit: 'W', value_min: 0, value_max: 1000, default_value: 500,
    scope: batteryScope,
    alwaysReport: true,
    warning: 'La batterie passe en mode piloté et reçoit cette consigne jusqu\'au retour '
      + 'automatique. Chaque exécution est tracée au journal.',
    run: (ids, watts) => bulkBatteryCommand(ids, 'charge', watts),
  },
  decharge: {
    label: 'Démarrer décharge',
    family: 'value',
    value_unit: 'W', value_min: 0, value_max: 800, default_value: 500,
    scope: batteryScope,
    alwaysReport: true,
    warning: 'La batterie passe en mode piloté et débite jusqu\'au retour automatique. '
      + 'Chaque exécution est tracée au journal.',
    run: (ids, watts) => bulkBatteryCommand(ids, 'decharge', watts),
  },
  auto: {
    label: 'Retour au mode automatique',
    family: 'action',
    scope: batteryScope,
    alwaysReport: true,
    warning: 'Publie ems_mode=general : la batterie reprend sa régulation constructeur et '
      + 'cesse de suivre nos consignes.',
    run: ids => bulkBatteryCommand(ids, 'auto', null),
  },
  power_zero: {
    label: 'Puissance 0 W',
    family: 'action',
    scope: batteryScope,
    alwaysReport: true,
    tone: 'danger',
    warning: 'Mode piloté avec consigne 0 W : la batterie reste en ligne mais n\'échange '
      + 'plus rien. Le matériel n\'expose pas de coupure franche.',
    run: ids => bulkBatteryCommand(ids, 'power_zero', null),
  },
  tuya_recheck: {
    label: 'Re-tester la connexion Tuya',
    family: 'action',
    scope: deviceScope,
    warning: 'Interroge chaque équipement pour rafraîchir son statut de connexion. '
      + 'Sans effet sur son fonctionnement.',
    run: ids => bulkTuyaRecheck(ids),
  },
  tuya_unlock: {
    label: 'Déverrouiller les thermostats',
    family: 'action',
    scope: deviceScope,
    alwaysReport: true,
    warning: 'Le client reprend la main sur son thermostat. À ne faire qu\'une fois le '
      + 'dossier complet.',
    run: ids => bulkTuyaUnlock(ids),
  },
  edit: {
    label: 'Modifier les informations',
    family: 'form',
    default_value: { marque: '', modele: '', statut_mandat: '' },
    renderForm: renderEditForm,
    warning: 'Les champs renseignés écrasent la valeur actuelle sur tous les actifs '
      + 'sélectionnés.',
    run: (ids, form) => {
      const patch = Object.fromEntries(
        Object.entries(form || {}).filter(([, v]) => v !== '' && v != null));
      if (!Object.keys(patch).length) {
        return Promise.reject(new Error('Aucun champ à modifier'));
      }
      return bulkUpdateAssets(ids, patch);
    },
  },
  mark_disconnected: {
    label: 'Marquer déconnecté',
    family: 'action',
    warning: 'Étiquette de gestion de parc : le matériel n\'est PAS coupé. Le statut '
      + 'repassera à « connecté » à la prochaine remontée de télémétrie.',
    run: ids => bulkUpdateAssets(ids, { statut_connexion: 'deconnecte' }),
  },
  coupure_materielle: {
    label: 'Coupure matérielle',
    family: 'action',
    unavailable: 'Indisponible sur Hoymiles 4020 X/AC : les topics output_power et reboot '
      + 'ne sont pas exposés par ce modèle. À revalider au RDV Hoymiles.',
    run: () => Promise.reject(new Error('Action indisponible')),
  },
  delete: {
    label: 'Supprimer les actifs',
    family: 'action',
    tone: 'danger',
    alwaysReport: true,
    warning: 'Suppression définitive. Les actifs et leur historique de monitoring '
      + 'disparaissent, aucune restauration n\'est possible depuis l\'interface.',
    run: ids => bulkDeleteAssets(ids),
  },
};

export default ASSET_BULK_ACTIONS;
